import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logSystemEvent } from "@/lib/logging/logSystemEvent";
import type { FavoriteKind, FavoriteItem, FavoriteTogglePayload } from "@/lib/favorites/types";
import {
  loadLocalFavorites,
  saveLocalFavorites,
  toggleLocalFavorite,
  getActiveFavorites,
  isFavoritedLocal,
} from "@/lib/favorites/localFavorites";
import { syncFavorites, toggleServerFavorite } from "@/lib/favorites/favoritesSync";

interface FavoritesContextValue {
  isFavorited: (kind: FavoriteKind, entityId: string) => boolean;
  toggleFavorite: (payload: FavoriteTogglePayload) => void;
  listFavorites: (kind?: FavoriteKind) => FavoriteItem[];
  isReady: boolean;
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

export function FavoritesProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<FavoriteItem[]>(() => loadLocalFavorites());
  const [userId, setUserId] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const inflightByKeyRef = useRef<Map<string, Promise<void>>>(new Map());

  // Auth listener
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserId(data.user?.id ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Sync on auth
  useEffect(() => {
    if (userId) {
      syncFavorites(userId)
        .then((synced) => setItems(synced))
        .catch((error) => {
          logSystemEvent({
            level: "error",
            source: "ui",
            eventKey: "favorites.sync.initial_failed",
            message: "Initial favorites sync failed",
            domain: "favorites",
            context: { user_id: userId, error: error instanceof Error ? error.message : String(error) },
          });
        })
        .finally(() => setIsReady(true));
    } else {
      setItems(loadLocalFavorites());
      setIsReady(true);
    }
  }, [userId]);

  // Persist local changes
  useEffect(() => {
    saveLocalFavorites(items);
  }, [items]);

  const enqueueServerSync = useCallback(
    (uid: string, payload: FavoriteTogglePayload, expectedFavorited: boolean) => {
      const key = `${payload.kind}:${payload.entityId}`;
      const previous = inflightByKeyRef.current.get(key) ?? Promise.resolve();

      const next = previous
        .catch(() => undefined)
        .then(async () => {
          try {
            const rpcResult = await toggleServerFavorite(
              uid,
              payload.kind,
              payload.entityId,
              expectedFavorited,
              payload
            );

            if (rpcResult.is_favorited !== expectedFavorited) {
              await logSystemEvent({
                level: "warn",
                source: "ui",
                eventKey: "favorites.toggle.rpc_mismatch",
                message: "toggle_favorite RPC returned unexpected state",
                domain: "favorites",
                entityType: payload.kind,
                entityId: payload.entityId,
                context: {
                  expected_favorited: expectedFavorited,
                  rpc_is_favorited: rpcResult.is_favorited,
                },
              });
            }
          } catch (error) {
            await logSystemEvent({
              level: "error",
              source: "ui",
              eventKey: "favorites.toggle.sync_failed",
              message: "Favorite toggle sync failed",
              domain: "favorites",
              entityType: payload.kind,
              entityId: payload.entityId,
              context: {
                expected_favorited: expectedFavorited,
                error: error instanceof Error ? error.message : String(error),
              },
            });
          }

          try {
            const canonical = await syncFavorites(uid);
            const canonicalHasEntity = canonical.some(
              (item) => item.kind === payload.kind && item.entityId === payload.entityId
            );

            if (canonicalHasEntity !== expectedFavorited) {
              await logSystemEvent({
                level: "warn",
                source: "ui",
                eventKey: "favorites.toggle.reconcile_mismatch",
                message: "Favorites reconcile mismatch after toggle",
                domain: "favorites",
                entityType: payload.kind,
                entityId: payload.entityId,
                context: {
                  expected_favorited: expectedFavorited,
                  canonical_has_entity: canonicalHasEntity,
                },
              });
            }

            setItems(canonical);
          } catch (error) {
            await logSystemEvent({
              level: "error",
              source: "ui",
              eventKey: "favorites.sync.reconcile_failed",
              message: "Favorites reconcile failed after toggle",
              domain: "favorites",
              entityType: payload.kind,
              entityId: payload.entityId,
              context: {
                error: error instanceof Error ? error.message : String(error),
              },
            });
          }
        })
        .finally(() => {
          if (inflightByKeyRef.current.get(key) === next) {
            inflightByKeyRef.current.delete(key);
          }
        });

      inflightByKeyRef.current.set(key, next);
    },
    []
  );

  const toggleFavorite = useCallback(
    (payload: FavoriteTogglePayload) => {
      let added = false;
      setItems((currentItems) => {
        const result = toggleLocalFavorite(currentItems, payload);
        added = result.added;
        return result.items;
      });

      if (userId) {
        enqueueServerSync(userId, payload, added);
      }
    },
    [userId, enqueueServerSync]
  );

  const isFavorited = useCallback(
    (kind: FavoriteKind, entityId: string) => isFavoritedLocal(items, kind, entityId),
    [items]
  );

  const listFavorites = useCallback(
    (kind?: FavoriteKind) => {
      const active = getActiveFavorites(items);
      const filtered = kind ? active.filter((i) => i.kind === kind) : active;
      return filtered.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());
    },
    [items]
  );

  return (
    <FavoritesContext.Provider value={{ isFavorited, toggleFavorite, listFavorites, isReady }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavorites must be used within FavoritesProvider");
  return ctx;
}
